/**
 * Note: This form is using card from Stripe Elements https://stripe.com/docs/stripe-js#elements
 * Card is not a Final Form field so it's not available trough Final Form.
 * It's also handled separately in handleSubmit function.
 */
import classNames from 'classnames';
import { bool, func, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { Form as FinalForm } from 'react-final-form';

import { ensurePaymentMethodCard } from '../../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';

import {
  FieldCheckbox,
  FieldTextInput,
  Form,
  Heading,
  SavedCardDetails,
  StripePaymentAddress,
} from '../../../components';

import ShippingDetails from '../ShippingDetails/ShippingDetails';

import css from './StripePaymentForm.module.css';

import { PrimaryButton } from '../../../components/mui/Button';
import { isIndividualSession } from '../../../util/soulwise-marketplace/listingType';
import { listingHasOnlineMode } from '../../../util/soulwise-marketplace/listingFields';
import {
  adoptWarmedCard,
  getStripeCardStyles,
  getWarmedStripe,
  stripeElementsOptions,
} from '../../../util/stripeWarmup';
/**
 * Translate a Stripe API error object.
 *
 * To keep up with possible keys from the Stripe API, see:
 *
 * https://stripe.com/docs/api#errors
 *
 * Note that at least at moment, the above link doesn't list all the
 * error codes that the API returns.
 *
 * @param {Object} intl - react-intl object from injectIntl
 * @param {Object} stripeError - error object from Stripe API
 *
 * @return {String} translation message for the specific Stripe error,
 * or the given error message (not translated) if the specific error
 * type/code is not defined in the translations
 *
 */
const stripeErrorTranslation = (intl, stripeError) => {
  const { message, code, type } = stripeError;

  if (!code || !type) {
    // Not a proper Stripe error object
    return intl.formatMessage({ id: 'StripePaymentForm.genericError' });
  }

  const translationId =
    type === 'validation_error'
      ? `StripePaymentForm.stripe.validation_error.${code}`
      : `StripePaymentForm.stripe.${type}`;

  return intl.formatMessage({
    id: translationId,
    defaultMessage: message,
  });
};

const cardStyles = getStripeCardStyles();

const OneTimePaymentWithCardElement = props => {
  const {
    cardClasses,
    formId,
    handleStripeElementRef,
    hasCardError,
    error,
    label,
    intl,
    marketplaceName,
  } = props;
  const labelText =
    label || intl.formatMessage({ id: 'StripePaymentForm.saveAfterOnetimePayment' });
  return (
    <React.Fragment>
      <label className={css.paymentLabel} htmlFor={`${formId}-card`}>
        <FormattedMessage id="StripePaymentForm.paymentCardDetails" />
      </label>
      <div
        className={cardClasses}
        id={`${formId}-card`}
        data-testid="stripe-card-element"
        ref={handleStripeElementRef}
      />
      {hasCardError ? <span className={css.error}>{error}</span> : null}
      <div className={css.saveForLaterUse}>
        <FieldCheckbox
          className={css.saveForLaterUseCheckbox}
          textClassName={css.saveForLaterUseLabel}
          id="saveAfterOnetimePayment"
          name="saveAfterOnetimePayment"
          label={labelText}
          value="saveAfterOnetimePayment"
          useSuccessColor
        />
        <span className={css.saveForLaterUseLegalInfo}>
          <FormattedMessage
            id="StripePaymentForm.saveforLaterUseLegalInfo"
            values={{ marketplaceName }}
          />
        </span>
      </div>
    </React.Fragment>
  );
};

const PaymentMethodSelector = props => {
  const {
    cardClasses,
    formId,
    changePaymentMethod,
    defaultPaymentMethod,
    handleStripeElementRef,
    hasCardError,
    error,
    paymentMethod,
    intl,
    marketplaceName,
  } = props;
  const last4Digits = defaultPaymentMethod.attributes.card.last4Digits;
  const labelText = intl.formatMessage(
    { id: 'StripePaymentForm.replaceAfterOnetimePayment' },
    { last4Digits }
  );

  return (
    <React.Fragment>
      <SavedCardDetails
        className={css.paymentMethodSelector}
        card={defaultPaymentMethod.attributes.card}
        onChange={changePaymentMethod}
      />
      {paymentMethod === 'replaceCard' ? (
        <OneTimePaymentWithCardElement
          cardClasses={cardClasses}
          formId={formId}
          handleStripeElementRef={handleStripeElementRef}
          hasCardError={hasCardError}
          error={error}
          label={labelText}
          intl={intl}
          marketplaceName={marketplaceName}
        />
      ) : null}
    </React.Fragment>
  );
};

const getPaymentMethod = (selectedPaymentMethod, hasDefaultPaymentMethod) => {
  return selectedPaymentMethod == null && hasDefaultPaymentMethod
    ? 'defaultCard'
    : selectedPaymentMethod == null
    ? 'onetimeCardPayment'
    : selectedPaymentMethod;
};

// Should we show onetime payment fields and does StripeElements card need attention
const checkOnetimePaymentFields = (
  cardValueValid,
  selectedPaymentMethod,
  hasDefaultPaymentMethod,
  hasHandledCardPayment
) => {
  const useDefaultPaymentMethod =
    selectedPaymentMethod === 'defaultCard' && hasDefaultPaymentMethod;
  // Billing details are known if we have already handled card payment or existing default payment method is used.
  const billingDetailsKnown = hasHandledCardPayment || useDefaultPaymentMethod;

  // If onetime payment is used, check that the StripeElements card has valid value.
  const oneTimePaymentMethods = ['onetimeCardPayment', 'replaceCard'];
  const useOnetimePaymentMethod = oneTimePaymentMethods.includes(selectedPaymentMethod);
  const onetimePaymentNeedsAttention =
    !billingDetailsKnown && !(useOnetimePaymentMethod && cardValueValid);

  return {
    onetimePaymentNeedsAttention,
    showOnetimePaymentFields: useOnetimePaymentMethod,
  };
};

const LocationOrShippingDetails = props => {
  const {
    askShippingDetails,
    showPickUplocation,
    listingLocation,
    formApi,
    locale,
    isBooking,
    isFuzzyLocation,
    isOnlineListing,
    intl,
  } = props;

  const locationDetails = listingLocation?.building
    ? `${listingLocation.building}, ${listingLocation.address}`
    : listingLocation?.address
    ? listingLocation.address
    : intl.formatMessage({ id: 'StripePaymentForm.locationUnknown' });

  return askShippingDetails ? (
    <ShippingDetails intl={intl} formApi={formApi} locale={locale} />
  ) : !isBooking && showPickUplocation ? (
    <div className={css.locationWrapper}>
      <Heading as="h3" rootClassName={css.heading}>
        <FormattedMessage id="StripePaymentForm.pickupDetailsTitle" />
      </Heading>
      <p className={css.locationDetails}>{locationDetails}</p>
    </div>
  ) : isBooking && !isFuzzyLocation && !isOnlineListing ? (
    <div className={css.locationWrapper}>
      <Heading as="h3" rootClassName={css.heading}>
        <FormattedMessage id="StripePaymentForm.locationDetailsTitle" />
      </Heading>
      <p className={css.locationDetails}>{locationDetails}</p>
    </div>
  ) : null;
};

// paymentRequest.canMakePayment() waits on Apple Pay / Payment Request
// merchant validation. When that fails (unverified domain, no wallet, or a
// hanging Stripe controller after remount) the shared Elements iframe stays
// blank until the browser times out (~30-40s). Never block the card on it.
export const WALLET_CAN_MAKE_PAYMENT_TIMEOUT_MS = 2500;

const initialState = {
  error: null,
  cardValueValid: false,
  // The mode can be 'onetimePayment', 'defaultCard', or 'replaceCard'
  // Check SavedCardDetails component for more information
  paymentMethod: null,
  canMakePayment: false,
};

/**
 * Payment form that asks for credit card info using Stripe Elements.
 *
 * When the card is valid and the user submits the form, a request is
 * sent to the Stripe API to handle payment. `stripe.confirmCardPayment`
 * may ask more details from cardholder if 3D security steps are needed.
 *
 * See: https://stripe.com/docs/payments/payment-intents
 *      https://stripe.com/docs/elements
 */
class StripePaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.updateBillingDetailsToMatchShippingAddress = this.updateBillingDetailsToMatchShippingAddress.bind(
      this
    );
    this.handleCardValueChange = this.handleCardValueChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.paymentForm = this.paymentForm.bind(this);
    this.initializeStripeElement = this.initializeStripeElement.bind(this);
    this.handleStripeElementRef = this.handleStripeElementRef.bind(this);
    this.handleCardNumberRef = this.handleCardNumberRef.bind(this);
    this.handleCardExpiryRef = this.handleCardExpiryRef.bind(this);
    this.handleCardCvcRef = this.handleCardCvcRef.bind(this);
    this.handleSplitCardChange = this.handleSplitCardChange.bind(this);
    this.changePaymentMethod = this.changePaymentMethod.bind(this);
    this.initializePaymentRequest = this.initializePaymentRequest.bind(this);
    this.schedulePaymentRequestInit = this.schedulePaymentRequestInit.bind(this);
    this.mountPaymentRequestButton = this.mountPaymentRequestButton.bind(this);
    this.finalFormAPI = null;
    this.cardContainer = null;
    this.cardNumberContainer = null;
    this.cardExpiryContainer = null;
    this.cardCvcContainer = null;
    this.cardNumber = null;
    this.cardExpiry = null;
    this.cardCvc = null;
    this.splitComplete = { number: false, expiry: false, cvc: false };
    this.paymentRequestContainer = null;
    this.elements = null;
    this.paymentRequest = null;
    this.paymentRequestButton = null;
    this.paymentRequestInitialized = false;
    this.paymentRequestInitTimer = null;
    this.canMakePaymentTimer = null;
    this.warmedCard = null;
  }

  componentDidMount() {
    if (!window.Stripe) {
      throw new Error('Stripe must be loaded for StripePaymentForm');
    }

    const publishableKey = this.props.stripePublishableKey;
    if (publishableKey) {
      const {
        onStripeInitialized,
        hasHandledCardPayment,
        defaultPaymentMethod,
        compactPayment,
      } = this.props;
      const warmed = getWarmedStripe(publishableKey);
      if (warmed?.stripe) {
        this.stripe = warmed.stripe;
        // Subscription compactPayment uses cardNumber/expiry/cvc. Stripe
        // forbids mixing those with a warmed single `card` Element on the
        // same Elements instance — that freezes/breaks the checkout page.
        if (!compactPayment) {
          this.elements = warmed.elements || null;
          this.warmedCard = warmed.card || null;
        }
      } else {
        this.stripe = window.Stripe(publishableKey);
      }
      onStripeInitialized(this.stripe);

      // Mount the card immediately — do not wait for speculate or the
      // Stripe customer fetch. canMakePayment() can occupy Stripe.js's
      // shared controller iframe for ~40s on remount.
      if (!(hasHandledCardPayment || defaultPaymentMethod)) {
        this.initializeStripeElement();
      } else if (hasHandledCardPayment || defaultPaymentMethod) {
        this.schedulePaymentRequestInit();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { loadingData, hasHandledCardPayment, defaultPaymentMethod } = this.props;
    const finishedLoadingStripeCustomer = prevProps.loadingData && !loadingData;
    const shouldMountCardElement =
      this.stripe && !this.card && !(hasHandledCardPayment || defaultPaymentMethod);

    if (finishedLoadingStripeCustomer && shouldMountCardElement) {
      if (this.cardContainer) {
        this.initializeStripeElement(this.cardContainer);
      }
    } else if (finishedLoadingStripeCustomer && (hasHandledCardPayment || defaultPaymentMethod)) {
      this.schedulePaymentRequestInit();
    }

    const payinTotal =
      this.props.walletPayinTotal || this.props.speculatedTransaction?.attributes?.payinTotal;
    if (
      this.stripe &&
      !this.paymentRequestInitialized &&
      !this.paymentRequestInitTimer &&
      payinTotal?.amount != null
    ) {
      this.schedulePaymentRequestInit();
    }

    // Mount wallet button once the container exists after canMakePayment flips true.
    if (
      this.state.canMakePayment &&
      this.paymentRequest &&
      this.paymentRequestContainer &&
      !this.paymentRequestButton
    ) {
      this.mountPaymentRequestButton(this.paymentRequest);
    }
  }

  schedulePaymentRequestInit() {
    if (this.paymentRequestInitialized || this.paymentRequestInitTimer) {
      return;
    }
    this.paymentRequestInitTimer = window.setTimeout(() => {
      this.paymentRequestInitTimer = null;
      this.initializePaymentRequest();
    }, 0);
  }

  initializePaymentRequest() {
    if (this.paymentRequestInitialized) {
      return;
    }
    const {
      speculatedTransaction,
      marketplaceName,
      stripeAccountCountry,
      walletPayinTotal,
    } = this.props;
    const payinTotal = walletPayinTotal || speculatedTransaction?.attributes?.payinTotal;

    if (!this.stripe || typeof this.stripe.paymentRequest !== 'function') {
      this.paymentRequestInitialized = true;
      return;
    }
    // Amount 0 (subscription trial due-today) is invalid for Payment Request;
    // mark initialized so we do not retry forever on every didUpdate.
    if (!payinTotal || payinTotal.amount == null || payinTotal.amount <= 0) {
      this.paymentRequestInitialized = true;
      return;
    }

    this.paymentRequestInitialized = true;
    const paymentRequest = this.stripe.paymentRequest({
      country: stripeAccountCountry || 'US',
      currency: payinTotal.currency.toLowerCase(),
      total: { label: marketplaceName, amount: payinTotal.amount },
      requestPayerName: true,
      requestPayerEmail: true,
    });
    this.paymentRequest = paymentRequest;

    const canMakePaymentPromise = Promise.resolve()
      .then(() => paymentRequest.canMakePayment())
      .catch(() => null);
    const timeoutPromise = new Promise(resolve => {
      this.canMakePaymentTimer = window.setTimeout(() => {
        this.canMakePaymentTimer = null;
        resolve(null);
      }, WALLET_CAN_MAKE_PAYMENT_TIMEOUT_MS);
    });
    Promise.race([canMakePaymentPromise, timeoutPromise]).then(result => {
      if (this.canMakePaymentTimer) {
        window.clearTimeout(this.canMakePaymentTimer);
        this.canMakePaymentTimer = null;
      }
      if (result) {
        this.setState({ canMakePayment: true });
      }
    });

    paymentRequest.on('paymentmethod', ev => {
      // Pass to parent via onSubmit with wallet flag
      this.props.onSubmit({
        card: null,
        message: null,
        paymentMethod: 'walletPayment',
        formValues: this.finalFormAPI?.getState()?.values || {},
        walletPaymentMethod: ev.paymentMethod,
        walletEvent: ev,
      });
    });
  }

  mountPaymentRequestButton(paymentRequest) {
    if (!this.paymentRequestContainer || this.paymentRequestButton) return;

    // Reuse the same Elements instance as the card field when available.
    const elements = this.elements || this.stripe.elements(stripeElementsOptions);
    this.elements = elements;
    const prButton = elements.create('paymentRequestButton', {
      paymentRequest,
      style: {
        paymentRequestButton: {
          type: 'default',
          theme: this.props.compactPayment ? 'dark' : 'light',
          height: '48px',
        },
      },
    });
    prButton.mount(this.paymentRequestContainer);
    this.paymentRequestButton = prButton;
  }

  unmountSplitCardElements() {
    if (this.cardNumber) {
      this.cardNumber.unmount();
      this.cardNumber = null;
    }
    if (this.cardExpiry) {
      this.cardExpiry.unmount();
      this.cardExpiry = null;
    }
    if (this.cardCvc) {
      this.cardCvc.unmount();
      this.cardCvc = null;
    }
    this.card = null;
    this.splitComplete = { number: false, expiry: false, cvc: false };
  }

  componentWillUnmount() {
    if (this.paymentRequestInitTimer) {
      window.clearTimeout(this.paymentRequestInitTimer);
      this.paymentRequestInitTimer = null;
    }
    if (this.canMakePaymentTimer) {
      window.clearTimeout(this.canMakePaymentTimer);
      this.canMakePaymentTimer = null;
    }
    if (this.cardNumber || this.cardExpiry || this.cardCvc) {
      this.unmountSplitCardElements();
    } else if (this.card) {
      this.card.removeEventListener('change', this.handleCardValueChange);
      this.card.unmount();
      this.card = null;
    }
    if (this.paymentRequestButton) {
      this.paymentRequestButton.unmount();
      this.paymentRequestButton = null;
    }
  }

  initializeCompactCardElements() {
    if (!this.cardNumberContainer || !this.cardExpiryContainer || !this.cardCvcContainer) {
      return;
    }
    if (this.cardNumber) {
      return;
    }
    // Always use a fresh Elements group for split fields so a warmed
    // `card` Element from listing/checkout prefetch cannot conflict.
    this.elements = this.stripe.elements(stripeElementsOptions);
    this.warmedCard = null;
    this.cardNumber = this.elements.create('cardNumber', { style: cardStyles, showIcon: true });
    this.cardExpiry = this.elements.create('cardExpiry', { style: cardStyles });
    this.cardCvc = this.elements.create('cardCvc', { style: cardStyles });
    this.cardNumber.mount(this.cardNumberContainer);
    this.cardExpiry.mount(this.cardExpiryContainer);
    this.cardCvc.mount(this.cardCvcContainer);
    this.card = this.cardNumber;
    this.cardNumber.on('change', event => this.handleSplitCardChange('number', event));
    this.cardExpiry.on('change', event => this.handleSplitCardChange('expiry', event));
    this.cardCvc.on('change', event => this.handleSplitCardChange('cvc', event));
    this.schedulePaymentRequestInit();
  }

  initializeStripeElement(element) {
    if (this.props.compactPayment && !element) {
      this.initializeCompactCardElements();
      return;
    }

    const mountNode = element || this.cardContainer;
    if (!mountNode) {
      return;
    }

    if (!this.elements) {
      this.elements = this.stripe.elements(stripeElementsOptions);
    }

    if (!this.card) {
      const adopted = adoptWarmedCard(mountNode);
      this.warmedCard = null;
      if (adopted?.card) {
        this.card = adopted.card;
        this.elements = adopted.elements || this.elements;
      } else {
        this.card = this.elements.create('card', { style: cardStyles });
        this.card.mount(mountNode);
      }
      this.card.addEventListener('change', this.handleCardValueChange);
      this.schedulePaymentRequestInit();
      // EventListener is the only way to simulate breakpoints with Stripe.
      window.addEventListener('resize', () => {
        if (this.card && !this.cardNumber) {
          if (window.innerWidth < 768) {
            this.card.update({ style: { base: { fontSize: '14px', lineHeight: '24px' } } });
          } else {
            this.card.update({ style: { base: { fontSize: '18px', lineHeight: '24px' } } });
          }
        }
      });
    }
  }

  updateBillingDetailsToMatchShippingAddress(shouldFill) {
    const formApi = this.finalFormAPI;
    const values = formApi.getState()?.values || {};
    formApi.batch(() => {
      formApi.change('name', shouldFill ? values.recipientName : '');
      formApi.change('addressLine1', shouldFill ? values.recipientAddressLine1 : '');
      formApi.change('addressLine2', shouldFill ? values.recipientAddressLine2 : '');
      formApi.change('postal', shouldFill ? values.recipientPostal : '');
      formApi.change('city', shouldFill ? values.recipientCity : '');
      formApi.change('state', shouldFill ? values.recipientState : '');
      formApi.change('country', shouldFill ? values.recipientCountry : '');
    });
  }

  changePaymentMethod(changedTo) {
    if (changedTo === 'defaultCard' && (this.card || this.cardNumber)) {
      if (this.cardNumber) {
        this.unmountSplitCardElements();
      } else if (this.card) {
        this.card.removeEventListener('change', this.handleCardValueChange);
        this.card.unmount();
        this.card = null;
      }
      this.setState({ cardValueValid: false });
    }
    this.setState({ paymentMethod: changedTo });
    if (changedTo === 'defaultCard' && this.finalFormAPI) {
      this.finalFormAPI.change('sameAddressCheckbox', undefined);
    } else if (changedTo === 'replaceCard' && this.finalFormAPI) {
      this.finalFormAPI.change('sameAddressCheckbox', ['sameAddress']);
      this.updateBillingDetailsToMatchShippingAddress(true);
    }
  }

  handleStripeElementRef(el) {
    this.cardContainer = el;
    if (!el) {
      // Container unmounted (e.g. loading spinner). Drop the Element so a later
      // remount creates a fresh mounted card instead of an orphaned reference.
      if (this.card && !this.cardNumber) {
        this.card.removeEventListener('change', this.handleCardValueChange);
        this.card.unmount();
        this.card = null;
      }
      return;
    }
    if (this.stripe) {
      this.initializeStripeElement(el);
    }
  }

  handleCardNumberRef(el) {
    this.cardNumberContainer = el;
    if (!el && this.cardNumber) {
      this.unmountSplitCardElements();
      return;
    }
    if (this.stripe) {
      this.initializeCompactCardElements();
    }
  }

  handleCardExpiryRef(el) {
    this.cardExpiryContainer = el;
    if (this.stripe) {
      this.initializeCompactCardElements();
    }
  }

  handleCardCvcRef(el) {
    this.cardCvcContainer = el;
    if (this.stripe) {
      this.initializeCompactCardElements();
    }
  }

  handleSplitCardChange(field, event) {
    const { intl } = this.props;
    this.splitComplete[field] = !!event.complete;
    this.setState({
      error: event.error ? stripeErrorTranslation(intl, event.error) : null,
      cardValueValid:
        this.splitComplete.number && this.splitComplete.expiry && this.splitComplete.cvc,
    });
  }

  handleCardValueChange(event) {
    const { intl } = this.props;
    const { error, complete } = event;

    const postalCode = event.value.postalCode;
    if (this.finalFormAPI) {
      this.finalFormAPI.change('postal', postalCode);
    }

    this.setState(prevState => {
      return {
        error: error ? stripeErrorTranslation(intl, error) : null,
        cardValueValid: complete,
      };
    });
  }
  handleSubmit(values) {
    const {
      onSubmit,
      inProgress,
      formId,
      hasHandledCardPayment,
      defaultPaymentMethod,
    } = this.props;
    const { initialMessage } = values;
    const { cardValueValid, paymentMethod } = this.state;
    const hasDefaultPaymentMethod = defaultPaymentMethod?.id;
    const selectedPaymentMethod = getPaymentMethod(paymentMethod, hasDefaultPaymentMethod);
    const { onetimePaymentNeedsAttention } = checkOnetimePaymentFields(
      cardValueValid,
      selectedPaymentMethod,
      hasDefaultPaymentMethod,
      hasHandledCardPayment
    );

    if (inProgress || onetimePaymentNeedsAttention) {
      // Already submitting or card value incomplete/invalid
      return;
    }

    const params = {
      message: initialMessage ? initialMessage.trim() : null,
      card: this.card,
      formId,
      formValues: values,
      paymentMethod: getPaymentMethod(
        paymentMethod,
        ensurePaymentMethodCard(defaultPaymentMethod).id
      ),
    };
    onSubmit(params);
  }

  paymentForm(formRenderProps) {
    const {
      className,
      rootClassName,
      inProgress: submitInProgress,
      loadingData,
      formId,
      paymentInfo,
      authorDisplayName,
      showInitialMessageInput,
      submitMessageId,
      intl,
      initiateOrderError,
      confirmCardPaymentError,
      confirmPaymentError,
      invalid,
      handleSubmit,
      form: formApi,
      hasHandledCardPayment,
      defaultPaymentMethod,
      listingLocation,
      askShippingDetails,
      showPickUplocation,
      totalPrice,
      locale,
      stripePublishableKey,
      marketplaceName,
      isBooking,
      isFuzzyLocation,
      values,
      listing,
      showSubmitButton = true,
    } = formRenderProps;
    const compactPayment = !!(formRenderProps.compactPayment || this.props.compactPayment);

    this.finalFormAPI = formApi;

    const listingType = listing?.attributes?.publicData?.listingType;
    const isIndividual = isIndividualSession(listingType);

    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(defaultPaymentMethod);
    const billingDetailsNeeded = !(hasHandledCardPayment || confirmPaymentError);

    const { cardValueValid, paymentMethod } = this.state;
    const hasDefaultPaymentMethod = ensuredDefaultPaymentMethod.id;
    const selectedPaymentMethod = getPaymentMethod(paymentMethod, hasDefaultPaymentMethod);
    const { onetimePaymentNeedsAttention, showOnetimePaymentFields } = checkOnetimePaymentFields(
      cardValueValid,
      selectedPaymentMethod,
      hasDefaultPaymentMethod,
      hasHandledCardPayment
    );

    const submitDisabled = invalid || onetimePaymentNeedsAttention || submitInProgress;
    const hasCardError = this.state.error && !submitInProgress;
    const hasPaymentErrors = confirmCardPaymentError || confirmPaymentError;
    const classes = classNames(rootClassName || css.root, className);
    const cardClasses = classNames(css.card, {
      [css.cardSuccess]: this.state.cardValueValid,
      [css.cardError]: hasCardError,
    });

    // Note: totalPrice might not be available initially
    // when speculateTransaction call is in progress.
    const totalPriceMaybe = totalPrice || '';

    // TODO: confirmCardPayment can create all kinds of errors.
    // Currently, we provide translation support for one:
    // https://stripe.com/docs/error-codes
    const piAuthenticationFailure = 'payment_intent_authentication_failure';
    const paymentErrorMessage =
      confirmCardPaymentError && confirmCardPaymentError.code === piAuthenticationFailure
        ? intl.formatMessage({ id: 'StripePaymentForm.confirmCardPaymentError' })
        : confirmCardPaymentError
        ? confirmCardPaymentError.message
        : confirmPaymentError
        ? intl.formatMessage({ id: 'StripePaymentForm.confirmPaymentError' })
        : intl.formatMessage({ id: 'StripePaymentForm.genericError' });

    const billingDetailsNameLabel = intl.formatMessage({
      id: 'StripePaymentForm.billingDetailsNameLabel',
    });

    const billingDetailsNamePlaceholder = intl.formatMessage({
      id: 'StripePaymentForm.billingDetailsNamePlaceholder',
    });

    const messagePlaceholder = intl.formatMessage(
      { id: 'StripePaymentForm.messagePlaceholder' },
      { name: authorDisplayName }
    );

    const messageOptionalText = intl.formatMessage({
      id: 'StripePaymentForm.messageOptionalText',
    });

    const initialMessageLabel = intl.formatMessage(
      { id: 'StripePaymentForm.messageLabel' },
      { messageOptionalText: messageOptionalText }
    );

    // Asking billing address is recommended in PaymentIntent flow.
    // In CheckoutPage, we send name and email as billing details, but address only if it exists.
    const billingAddress = (
      <StripePaymentAddress
        intl={intl}
        form={formApi}
        fieldId={formId}
        card={this.card}
        locale={locale}
      />
    );

    const hasStripeKey = stripePublishableKey;

    const handleSameAddressCheckbox = event => {
      const checked = event.target.checked;
      this.updateBillingDetailsToMatchShippingAddress(checked);
    };
    const isBookingYesNo = isBooking ? 'yes' : 'no';

    return hasStripeKey ? (
      <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
        <LocationOrShippingDetails
          askShippingDetails={askShippingDetails}
          showPickUplocation={showPickUplocation}
          listingLocation={listingLocation}
          isBooking={isBooking}
          isFuzzyLocation={isFuzzyLocation}
          isOnlineListing={listingHasOnlineMode(listing)}
          formApi={formApi}
          locale={locale}
          intl={intl}
        />

        {showInitialMessageInput ? (
          <div>
            <Heading as="h3" rootClassName={css.heading}>
              <FormattedMessage id="StripePaymentForm.messageHeading" />
            </Heading>

            <FieldTextInput
              type="textarea"
              id={`${formId}-message`}
              name="initialMessage"
              supportingText={initialMessageLabel}
              placeholder={messagePlaceholder}
              className={css.message}
            />
          </div>
        ) : null}

        {billingDetailsNeeded ? (
          <React.Fragment>
            <Heading as="h3" rootClassName={css.heading}>
              <FormattedMessage id="StripePaymentForm.paymentHeading" />
            </Heading>

            {this.state.canMakePayment && (
              <div className={css.paymentRequestButtonContainer}>
                <p className={css.walletButtonLabel}>
                  <FormattedMessage
                    id={
                      compactPayment
                        ? 'StripePaymentForm.expressCheckoutLabel'
                        : 'StripePaymentForm.payAndConfirmWith'
                    }
                  />
                </p>
                <div ref={el => (this.paymentRequestContainer = el)} />
                <div className={css.orDivider}>
                  <span>
                    <FormattedMessage id="StripePaymentForm.orPayWithCard" />
                  </span>
                </div>
              </div>
            )}

            {hasDefaultPaymentMethod ? (
              <PaymentMethodSelector
                cardClasses={cardClasses}
                formId={formId}
                defaultPaymentMethod={ensuredDefaultPaymentMethod}
                changePaymentMethod={this.changePaymentMethod}
                handleStripeElementRef={this.handleStripeElementRef}
                hasCardError={hasCardError}
                error={this.state.error}
                paymentMethod={selectedPaymentMethod}
                intl={intl}
                marketplaceName={marketplaceName}
              />
            ) : compactPayment ? (
              <div className={css.compactCardFields}>
                <label className={css.compactCardLabel} htmlFor={`${formId}-card-number`}>
                  <FormattedMessage id="StripePaymentForm.cardNumberLabel" />
                </label>
                <div
                  className={cardClasses}
                  id={`${formId}-card-number`}
                  data-testid="stripe-card-element"
                  ref={this.handleCardNumberRef}
                />
                <div className={css.compactCardRow}>
                  <div>
                    <label className={css.compactCardLabel} htmlFor={`${formId}-card-expiry`}>
                      <FormattedMessage id="StripePaymentForm.cardExpiryLabel" />
                    </label>
                    <div
                      className={cardClasses}
                      id={`${formId}-card-expiry`}
                      ref={this.handleCardExpiryRef}
                    />
                  </div>
                  <div>
                    <label className={css.compactCardLabel} htmlFor={`${formId}-card-cvc`}>
                      <FormattedMessage id="StripePaymentForm.cardCvcLabel" />
                    </label>
                    <div
                      className={cardClasses}
                      id={`${formId}-card-cvc`}
                      ref={this.handleCardCvcRef}
                    />
                  </div>
                </div>
                {hasCardError ? <span className={css.error}>{this.state.error}</span> : null}
                <div className={css.saveForLaterUseCompact}>
                  <FieldCheckbox
                    className={css.saveForLaterUseCheckbox}
                    textClassName={css.saveForLaterUseLabel}
                    id="saveAfterOnetimePayment"
                    name="saveAfterOnetimePayment"
                    label={intl.formatMessage({
                      id: 'StripePaymentForm.saveAfterOnetimePayment',
                    })}
                    value="saveAfterOnetimePayment"
                    useSuccessColor
                  />
                </div>
              </div>
            ) : (
              <OneTimePaymentWithCardElement
                cardClasses={cardClasses}
                formId={formId}
                handleStripeElementRef={this.handleStripeElementRef}
                hasCardError={hasCardError}
                error={this.state.error}
                intl={intl}
                marketplaceName={marketplaceName}
              />
            )}

            {showOnetimePaymentFields ? (
              <div className={css.billingDetails}>
                <Heading as="h3" rootClassName={css.heading}>
                  <FormattedMessage id="StripePaymentForm.billingDetails" />
                </Heading>

                {askShippingDetails ? (
                  <FieldCheckbox
                    className={css.sameAddressCheckbox}
                    textClassName={css.sameAddressLabel}
                    id="sameAddressCheckbox"
                    name="sameAddressCheckbox"
                    label={intl.formatMessage({
                      id: 'StripePaymentForm.sameBillingAndShippingAddress',
                    })}
                    value="sameAddress"
                    useSuccessColor
                    onChange={handleSameAddressCheckbox}
                  />
                ) : null}

                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="name"
                  name="name"
                  autoComplete="cc-name"
                  label={billingDetailsNameLabel}
                  placeholder={billingDetailsNamePlaceholder}
                />

                {billingAddress}
              </div>
            ) : null}
          </React.Fragment>
        ) : null}

        {initiateOrderError ? (
          <span className={css.errorMessage}>{initiateOrderError.message}</span>
        ) : null}
        {showSubmitButton ? (
          <div className={css.submitContainer}>
            {hasPaymentErrors ? (
              <span className={css.errorMessage}>{paymentErrorMessage}</span>
            ) : null}
            <PrimaryButton
              sx={{ width: '100%', marginTop: '16px' }}
              type="submit"
              loading={submitInProgress}
              disabled={submitDisabled}
            >
              {submitMessageId ? (
                <FormattedMessage id={submitMessageId} />
              ) : billingDetailsNeeded ? (
                <FormattedMessage
                  id={
                    isIndividual
                      ? 'StripePaymentForm.individualSession.submitPaymentInfo'
                      : 'StripePaymentForm.submitPaymentInfo'
                  }
                  values={{ totalPrice: totalPriceMaybe, isBooking: isBookingYesNo }}
                />
              ) : (
                <FormattedMessage
                  id="StripePaymentForm.submitConfirmPaymentInfo"
                  values={{ totalPrice: totalPriceMaybe, isBooking: isBookingYesNo }}
                />
              )}
            </PrimaryButton>
            {submitMessageId ? null : (
              <p className={css.paymentInfo}>
                {isIndividual ? (
                  <FormattedMessage
                    id="StripePaymentForm.submitConfirmPaymentFinePrint"
                    values={{ isBooking: isBookingYesNo, name: authorDisplayName }}
                  />
                ) : (
                  <FormattedMessage id="StripePaymentForm.Soulwise-instant-booking.submitConfirmPaymentFinePrint" />
                )}
              </p>
            )}
          </div>
        ) : null}
      </Form>
    ) : (
      <div className={css.missingStripeKey}>
        <FormattedMessage id="StripePaymentForm.missingStripeKey" />
      </div>
    );
  }

  render() {
    const { onSubmit, ...rest } = this.props;
    return <FinalForm onSubmit={this.handleSubmit} {...rest} render={this.paymentForm} />;
  }
}

StripePaymentForm.defaultProps = {
  className: null,
  rootClassName: null,
  inProgress: false,
  loadingData: false,
  showSubmitButton: true,
  showInitialMessageInput: true,
  submitMessageId: null,
  compactPayment: false,
  hasHandledCardPayment: false,
  defaultPaymentMethod: null,
  initiateOrderError: null,
  confirmCardPaymentError: null,
  confirmPaymentError: null,
  askShippingDetails: false,
  showPickUplocation: false,
  listingLocation: null,
  totalPrice: null,
  isFuzzyLocation: false,
  speculatedTransaction: null,
};

StripePaymentForm.propTypes = {
  className: string,
  rootClassName: string,
  inProgress: bool,
  loadingData: bool,
  showSubmitButton: bool,
  initiateOrderError: object,
  confirmCardPaymentError: object,
  confirmPaymentError: object,
  formId: string.isRequired,
  onSubmit: func.isRequired,
  authorDisplayName: string.isRequired,
  showInitialMessageInput: bool,
  submitMessageId: string,
  compactPayment: bool,
  hasHandledCardPayment: bool,
  defaultPaymentMethod: propTypes.defaultPaymentMethod,
  askShippingDetails: bool,
  showPickUplocation: bool,
  listingLocation: shape({
    address: string.isRequired,
    building: string,
  }),
  totalPrice: string,
  locale: string.isRequired,
  stripePublishableKey: string.isRequired,
  stripeAccountCountry: string,
  marketplaceName: string.isRequired,
  isBooking: bool.isRequired,
  isFuzzyLocation: bool,
  listing: propTypes.listing,
  speculatedTransaction: propTypes.transaction,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(StripePaymentForm);
